// Read-only "Frais Financiers" summary for a student, reused by both the
// student VIEW and EDIT modals. Fetches the student's current-year financial
// account (fees, remises, échéancier, solde) and offers a deep-link to
// Gestion des Paiements where the full editing already lives.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Settings } from 'lucide-react';
import { adminFetchStudentAccounts, formatCurrency, type StudentFinancialAccount } from '@/services/financialService';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission, RoleRoutes } from '@/types/global.types';
import { useAuthStore } from '@/features/auth/stores/authStore';

interface StudentFinancialSectionProps {
  /** Student RECORD id (StudentUser.studentId), not the user id */
  studentId?: string | null;
  /** Student code, used to pre-filter the payments page on the manage link */
  studentCode?: string | null;
}

export const StudentFinancialSection: React.FC<StudentFinancialSectionProps> = ({ studentId, studentCode }) => {
  const [account, setAccount] = useState<StudentFinancialAccount | null>(null);
  const [loaded, setLoaded] = useState(false);

  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const user = useAuthStore((s) => s.user);
  const canView = hasPermission(Permission.FINANCE_PAYMENT_VIEW);
  const canManage = hasPermission(Permission.FINANCE_PAYMENT_RECORD);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!canView || !studentId) {
        setLoaded(true);
        return;
      }
      try {
        const accounts = await adminFetchStudentAccounts(studentId);
        if (cancelled) return;
        setAccount(accounts.find((a) => a.academicYear?.isCurrent) ?? accounts[0] ?? null);
      } catch {
        if (!cancelled) setAccount(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canView, studentId]);

  const goToManage = () => {
    if (!user?.role || !user.tenantId || !studentCode) return;
    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return;
    const base = roleRoute.replace(':tenantId', user.tenantId);
    const financeSeg = user.role === 'FINANCIAL_MANAGER' ? '/paiements' : '/gestion-financiere/paiements';
    navigate(`${base}${financeSeg}?search=${encodeURIComponent(studentCode)}`);
  };

  if (!canView) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between pb-2 border-b border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
          <Wallet size={16} className="text-gray-400" />
          Frais Financiers
        </h4>
        {canManage && studentCode && (
          <button
            type="button"
            onClick={goToManage}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
          >
            <Settings size={14} />
            Gérer les frais
          </button>
        )}
      </div>

      {!loaded ? (
        <p className="text-sm text-gray-400">Chargement…</p>
      ) : !account ? (
        <p className="text-sm text-gray-500">
          Aucun compte financier pour l'année en cours. Il sera créé automatiquement lorsque les
          frais de la filière seront configurés.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Total dû</p>
              <p className="text-sm font-bold text-gray-900">{formatCurrency(account.totalDue - account.totalDiscount)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Payé</p>
              <p className="text-sm font-bold text-green-600">{formatCurrency(account.totalPaid)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Remises</p>
              <p className="text-sm font-bold text-indigo-600">{formatCurrency(account.totalDiscount)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Solde</p>
              <p className={`text-sm font-bold ${account.balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>{formatCurrency(Math.max(0, account.balance))}</p>
            </div>
          </div>

          {account.feeItems && account.feeItems.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Détail des frais</p>
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                {account.feeItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm text-gray-700">{item.label}</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {account.discounts && account.discounts.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Remises / Bourses</p>
              <div className="flex flex-wrap gap-1.5">
                {account.discounts.map((d) => (
                  <span key={d.id} className="inline-flex px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-100">
                    {d.discountConfig?.name || 'Remise'} · {formatCurrency(d.discountAmount)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Échéancier de paiement</p>
            {account.schedule ? (
              <p className="text-sm text-gray-700">
                {account.schedule.template?.name || 'Échéancier personnalisé'}
                {account.schedule.installments && (
                  <span className="text-gray-400"> · {account.schedule.installments.length} échéance(s)</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-gray-500">Aucun échéancier assigné</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentFinancialSection;
